import React, { useState, useCallback, memo, useContext } from 'react'
import { Card, Typography, Box, CardMedia, Button } from '@mui/material'
import EditRubro from '../../widgets/EditarRubro'
import { deleteRubro } from '../../services/rubros.operaciones'
import ConfirmDialog from '../../../../Products/components/ConfirmDialog'
import { TenantsContext } from '../../../../../context/tenants.context'
import { GruposAtributos, MonedaList, UnidadDeMedidaList } from '../../../../Integraciones/types/responses'
import { Rubro } from '../../types/rubro'

interface CardRubroProps {
  rubros: Rubro[]
  rubro: Rubro
  isMobile: boolean
  monedas: MonedaList[]
  unidadesDeMedida: UnidadDeMedidaList[]
  gruposAtr: GruposAtributos[]
  updateRubros: () => void
  handleNodeSelect: (nodeId: string) => void
}

const CardRubro: React.FC<CardRubroProps> = ({
  rubros,
  isMobile,
  rubro,
  monedas,
  unidadesDeMedida,
  gruposAtr,
  updateRubros,
  handleNodeSelect
}) => {
  const isRootNode = !rubro.codRubroPadre
  const [updateRubro, setUpdateRubro] = useState<boolean>(false)
  const [confirmDialog, setConfirmDialog] = useState<any>({
    open: false,
    text: {
      title: '',
      body: '',
      leftButton: 'Cancelar',
      rightButton: 'Confirmar',
      colorButtonConfirm: 'primary'
    },
    action: async () => {}
  })
  const { tenant } = useContext(TenantsContext)

  const handleDeleteRubro = useCallback(
    (codRubro: any) => {
      setConfirmDialog({
        open: true,
        text: {
          title: '¿Deseás eliminar este rubro?',
          body: 'Esta accion no se puede deshacer',
          leftButton: 'Cancelar',
          rightButton: 'Confirmar',
          colorButtonConfirm: 'error'
        },
        action: async () => {
          await deleteRubro(codRubro, tenant)
          updateRubros()
          clearConfirmDialog()
        },
        close: () => clearConfirmDialog()
      })
    },
    [tenant]
  )

  const clearConfirmDialog = () => {
    setConfirmDialog((confirmDialog: any) => ({
      ...confirmDialog,
      open: false
    }))
  }

  /* if (isRootNode && !rubro.urlPortada) {
    return null
  } */

  return (
    <>
      <Card
        key={rubro.codRubro}
        variant="outlined"
        sx={{
          px: { md: '1em', xs: '0.5em' },
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          border: '0.5px solid',
          backgroundColor: 'lightgrey',
          marginTop: '2px',
          alignItems: 'center',
          width: '100%',
          gap: isMobile ? '2px' : '6px',
          ...(isMobile && {
            padding: '5px 5px 10px 5px'
          })
        }}
      >
        {rubro.urlPortada && (
          <CardMedia
            component="img"
            sx={{
              maxWidth: '55%',
              maxHeight: '400px',
              objectFit: 'contain'
            }}
            image={rubro.urlPortada}
            alt=""
          />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'row', md: rubro.urlPortada ? 'column' : 'row' },
            gap: '1rem',
            alignItems: 'center',
            width: { md: rubro.urlPortada ? '200px' : 'auto' }
          }}
        >
          {rubro.urlIcono && (
            <CardMedia
              component="img"
              sx={{
                width: { xs: '140px', md: rubro.urlPortada ? '100%' : '150px' },
                height: { xs: '190px', md: 'auto' },
                objectFit: 'contain'
              }}
              image={rubro.urlIcono}
              alt=""
            />
          )}
          {rubro.urlFoto && (
            <CardMedia
              component="img"
              sx={{
                width: { xs: '140px', md: rubro.urlPortada ? '100%' : '150px' },
                height: { xs: '190px', md: 'auto' },
                objectFit: 'contain'
              }}
              image={rubro.urlFoto}
              alt=""
            />
          )}
        </Box>
        <Box
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            fontSize: 16,
            fontFamily: 'Monospace',
            textAlign: 'left',
            marginTop: '16px',
            marginLeft: '10px'
          }}
        >
          <div key={rubro.codRubro}>
            {rubro.color && (
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  fontWeight: 'bold',
                  paddingBottom: '16px'
                }}
              >
                <>
                  {`Color: `}
                  <span
                    style={{
                      display: 'inline-block',
                      width: '16px',
                      height: '16px',
                      border: `1px solid black`,
                      backgroundColor: `#${rubro.color}` || '#000000',
                      marginLeft: '3px'
                    }}
                  ></span>
                </>
              </Typography>
            )}
            {rubro.nombreCompleto && (
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  fontWeight: 'bold',
                  paddingBottom: '16px'
                }}
              >
                {`Nombre Completo: ${rubro.nombreCompleto}`}
              </Typography>
            )}
            {rubro.buscadorImagenNombreCaption && (
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  fontWeight: 'bold',
                  paddingBottom: '16px'
                }}
              >
                {`Nombre (buscador imágenes): ${rubro.buscadorImagenNombreCaption}`}
              </Typography>
            )}
            {rubro.monedaList?.length > 0 && (
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  fontWeight: 'bold',
                  paddingBottom: '16px'
                }}
              >{`Moneda/s: ${rubro.monedaList
                .map((moneda: any) => `${moneda.nombre} (${moneda.simbolo})`)
                .join(', ')}`}</Typography>
            )}
            {rubro.atributosPermitidos?.length > 0 && (
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  fontWeight: 'bold',
                  paddingBottom: '16px'
                }}
              >{`Atributos: ${rubro.atributosPermitidos.map((p: any) => `${p.slug}`).join(', ')}`}</Typography>
            )}
            {rubro.unidadDeMedidaList?.length > 0 && (
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  fontWeight: 'bold',
                  paddingBottom: '16px'
                }}
              >{`Unidad/es de Medida: ${rubro.unidadDeMedidaList
                .map((element: any) => `${element.nombreSingular}/${element.nombrePlural}`)
                .join(', ')}`}</Typography>
            )}
            {rubro.precioMinimo > 0 && (
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  fontWeight: 'bold',
                  paddingBottom: '16px'
                }}
              >{`Precio Mínimo: ${rubro.precioMinimo}`}</Typography>
            )}
            {rubro.slug && (
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  fontWeight: 'bold',
                  paddingBottom: '16px'
                }}
              >{`Slug: ${rubro.slug}`}</Typography>
            )}
            {rubro.keywords && (
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  fontWeight: 'bold',
                  paddingBottom: '16px'
                }}
              >{`Keywords: ${rubro.keywords}`}</Typography>
            )}
            {rubro.orden && (
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  fontWeight: 'bold',
                  paddingBottom: '16px'
                }}
              >{`Orden: ${rubro.orden}`}</Typography>
            )}
          </div>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'row' : 'column',
            flexWrap: 'wrap',
            gap: '16px',
            ...(!isMobile && {
              alignItems: 'flex-end',
              ml: 'auto' // Empuja los botones hacia la derecha
            })
          }}
        >
          {/* Botón "Editar" */}
          <Button
            size={isMobile ? 'small' : 'medium'}
            variant="contained"
            color="primary"
            onClick={() => setUpdateRubro(true)}
          >
            Editar
          </Button>

          {/* Botón "Borrar" */}
          <Button
            size={isMobile ? 'small' : 'medium'}
            variant="contained"
            color="error"
            onClick={() => handleDeleteRubro(rubro.codRubro)}
          >
            Borrar
          </Button>
        </Box>
      </Card>

      <ConfirmDialog {...confirmDialog} />
      <EditRubro
        open={updateRubro}
        setOpen={setUpdateRubro}
        rubros={rubros}
        monedas={monedas}
        unidadesDeMedida={unidadesDeMedida}
        gruposAtr={gruposAtr}
        codRubro={rubro.codRubro}
        updateRubros={updateRubros}
        handleNodeSelect={handleNodeSelect}
      />
    </>
  )
}

export default memo(CardRubro)
